import { Box, Button, Grid, Link, List, ListItemButton, ListItemText, Stack, TextField, Typography, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import NoteClient, { NoteErrors } from "../apiClients/NoteClient";
import { NoteDisplay } from "../models/dtos";
import { useErrorDisplay } from '../components/ErrorDisplay';
import BusySpinner from "../components/BusySpinner";
import ConditionalDisplay from "../components/ConditionalDisplay";
import IconButtonEx from "../components/IconButtonEx";
import DeleteIcon from "@mui/icons-material/Delete";
import { I18N } from "../I18N";
import { FormatDate } from "../Helpers";
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import useViewportMode from "../hooks/useViewportMode";
import { MobileEntityCard } from "../components/responsive";

export interface NoteListComponentProps {
    associationId: string;
    subscriptionActive: boolean;
    data: NoteDisplay[];
    onChange?: () => Promise<void>;
}

const NoteListComponent = ({ associationId, subscriptionActive, data, onChange = async () => { } }: NoteListComponentProps) => {

    const defaultNote = NoteClient.CreateDefaultNoteDisplay();

    const [ErrorDisplay, setError] = useErrorDisplay();
    const [busy, setBusy] = useState<boolean>(false);
    const theme = useTheme();
    const viewport = useViewportMode();
    const singlePane = viewport.isPhone || (viewport.isTablet && viewport.isPortrait);

    const [listItemSelected, setListItemSelected] = useState<NoteDisplay>(defaultNote);
    const [showSelectedDetail, setShowSelectedDetail] = useState(false);

    useEffect(() => {
        // Set first item as selected by default
        if (data.length > 0) {
            setListItemSelected(data[0]);
            setShowSelectedDetail(false);
        }
        else {
            setListItemSelected(NoteClient.CreateDefaultNoteDisplay());
            setShowSelectedDetail(false);
        }
    }, [data]);

    const handleListItemClick = (e, i: string) => {
        let n = data.find(f => f.Id === i);
        setListItemSelected(n);
        if (singlePane)
            setShowSelectedDetail(true);
    };

    const handleDelete = async (id: string) => {
        try {
            setBusy(true);

            await new NoteClient().Delete(id);
            onChange();
        }
        catch (error) {
            setError(error, NoteErrors.Delete);
        }
        finally {
            setBusy(false);
        }
    }

    const getAlternateDisplayGridContainerStyle = () => {
        return { width: '100%', marginTop: theme.spacing(0) };
    }

    if (!subscriptionActive)
        if (!viewport.isDesktop)
            return <Box sx={{ p: 2 }}><Typography variant="body2">You need an active subscription for Notes, <Link href="/SubscriptionPage" target="_self" rel="noopener" underline="hover">subscribe here</Link></Typography></Box>;
        else
        return (
            <>
                <Grid container spacing={1} sx={getAlternateDisplayGridContainerStyle}>
                    <Grid item xs={3}></Grid>
                    <Grid item xs={9}>
                        <Typography sx={{ fontSize: 'small', marginTop: theme.spacing(2), marginBottom: theme.spacing(2) }}>
                            {`You need an active subscription for Notes, `}
                            <Link href="/SubscriptionPage" target="_self" rel="noopener" underline="hover">{`subscribe here`}</Link>
                        </Typography>
                        
                    </Grid>
                </Grid>
            </>
        )
        else if (singlePane)
    return (
        <Box>
            <ErrorDisplay />
            <BusySpinner busy={busy}>
                {showSelectedDetail && data.length > 0 ? (
                    <Stack spacing={2} sx={{ p: 2 }} data-testid="note-detail">
                        <Button
                            startIcon={<ArrowBackRoundedIcon />}
                            onClick={() => setShowSelectedDetail(false)}
                            sx={{ minHeight: 44, alignSelf: 'flex-start' }}
                        >
                            Notes
                        </Button>
                        <TextField label="Note" fullWidth name="Content" size="small" variant="outlined" multiline minRows={10} value={listItemSelected.Content} />
                        <TextField label="Entered by" fullWidth name="SystemUserId" size="small" variant="outlined" value={listItemSelected.SystemUserName} />
                        <TextField label="Entered Date" fullWidth name="LastUpdateDate" size="small" variant="outlined" value={FormatDate(listItemSelected.LastUpdateDate)} />
                    </Stack>
                ) : (
                    <Stack spacing={1.5} sx={{ p: 1.5 }} data-testid="note-cards">
                        {data.length <= 0 && <Typography variant="body2">Hm, there's that one thing I can't quite remember</Typography>}
                        {data.map(note => (
                            <MobileEntityCard
                                key={note.Id}
                                title={note.Title}
                                subtitle={FormatDate(note.LastUpdateDate)}
                                fields={[{ label: 'Entered by', value: note.SystemUserName, fullWidth: true }]}
                                actions={<>
                                    <Button onClick={(event) => handleListItemClick(event, note.Id)}>View</Button>
                                    <IconButtonEx
                                        hoverContent={I18N.Translate('Delete Note')}
                                        message={`${I18N.Translate('Are you sure you want to delete')} '${note.Title}'`}
                                        onClick={() => handleDelete(note.Id)}
                                    >
                                        <DeleteIcon htmlColor="red" />
                                    </IconButtonEx>
                                </>}
                                ariaLabel={`Note ${note.Title}`}
                            />
                        ))}
                    </Stack>
                )}
            </BusySpinner>
        </Box>
    );
        else
    return (
        <Box>
            <ErrorDisplay />
            <BusySpinner busy={busy}>
                <Grid container spacing={1} sx={{ width: '100%', marginTop: theme.spacing(0) }}>
                    {/* Note Title List */}
                    <Grid item xs={3}>
                        <Box sx={{ width: '100%', maxWidth: 360, bgcolor: 'background.paper', marginLeft: '.5em', marginRight: '.5em', marginTop: theme.spacing(2), marginBottom: theme.spacing(2) }}>
                            <List>
                                {data.map(n =>
                                    <ListItemButton key={n.Id}
                                        selected={listItemSelected.Id === n.Id}
                                        onClick={(event) => handleListItemClick(event, n.Id)}>
                                        <ListItemText primary={n.Title} />
                                        <ConditionalDisplay ifTrue={listItemSelected.Id === n.Id}>
                                            {/* <OnlyForRole role={}> */}
                                            <IconButtonEx
                                                size='small'
                                                hoverContent={I18N.Translate('Delete Note')}
                                                message={`${I18N.Translate('Are you sure you want to delete')} '${n.Title}'`}
                                                onClick={() => handleDelete(n.Id)}><DeleteIcon htmlColor='red' fontSize='small' />
                                            </IconButtonEx>
                                            {/* </OnlyForRole>) */}
                                        </ConditionalDisplay>
                                    </ListItemButton>
                                )}
                            </List>
                        </Box>
                    </Grid>
                    {/* Note Content */}
                    <Grid item xs={9} >
                        <ConditionalDisplay ifTrue={data.length <= 0}>
                            <Typography sx={{ fontSize: 'small', marginTop: theme.spacing(2), marginBottom: theme.spacing(2) }}>Hm, there's that one thing I can't quite remember</Typography>
                        </ConditionalDisplay>
                        <ConditionalDisplay ifTrue={data.length > 0}>
                            <Box sx={{ width: '100%', bgcolor: 'background.paper' }}>
                                <Stack sx={{ marginTop: theme.spacing(2), marginBottom: theme.spacing(3) }}>
                                    <TextField label="Note" fullWidth
                                        name="Content"
                                        sx={{ marginBottom: theme.spacing(2), fontSize: 'medium' }}
                                        size="small"
                                        variant="outlined"
                                        multiline={true}
                                        minRows={10}
                                        maxRows={10}
                                        value={listItemSelected.Content} />
                                    <TextField label="Entered by" fullWidth
                                        name="SystemUserId"
                                        sx={{ marginBottom: theme.spacing(2), fontSize: 'small' }}
                                        size="small"
                                        variant="outlined"
                                        value={listItemSelected.SystemUserName} />                                    
                                        
                                    <TextField label="Entered Date" fullWidth
                                        name="LastUpdateDate"
                                        sx={{ marginBottom: theme.spacing(2), fontSize: 'small' }}
                                        size="small"
                                        variant="outlined"
                                        value={FormatDate(listItemSelected.LastUpdateDate)} />
                                </Stack>
                            </Box>
                        </ConditionalDisplay>
                    </Grid>
                </Grid>
            </BusySpinner>
        </Box>
    )
};

export default NoteListComponent;
